import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, CATEGORIES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sparkles, Send, Loader2, ArrowLeft, MessageCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatErr } from "../context/AuthContext";
import { AttachButton, PendingAttachments, MessageAttachments } from "../components/ChatAttachments";
import RichTextEditor from "../components/RichTextEditor";

const STATUSES = ["nuovo", "in_lavorazione", "chiuso", "annullato"];

export default function AdminQuoteDetail() {
  const { id } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    if (!loading && (!user || (user.role !== "personale" && user.role !== "admin"))) navigate("/login");
  }, [user, loading, navigate]);

  const load = async () => {
    try {
      const r = await api.get(`/admin/quotes/${id}`);
      setQuote(r.data);
    } catch { toast.error("Non trovato"); }
  };
  useEffect(() => { if (user?.role === "personale" || user?.role === "admin") load(); /* eslint-disable-next-line */ }, [id, user]);

  const update = async (patch) => {
    const r = await api.patch(`/admin/quotes/${id}`, patch);
    setQuote(r.data);
    toast.success("Aggiornato");
  };

  if (!quote) return <div className="min-h-screen"><Navbar/><div className="p-12 text-center text-slate-500">Caricamento...</div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <Link to="/admin/preventivi" className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-800 mb-4 text-sm" data-testid="admin-back-link">
          <ArrowLeft className="w-4 h-4"/> Tutti i preventivi
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <div className="text-xs font-mono text-slate-500">{quote.quote_id}</div>
            <h1 className="heading text-3xl font-extrabold text-slate-900">{quote.name}</h1>
            <div className="text-slate-600 text-sm mt-1">{quote.email || "—"} · {quote.phone || "—"}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => setChatOpen(true)} variant="outline" data-testid="admin-chat-open"><MessageCircle className="w-4 h-4 mr-2"/>Chat cliente</Button>
            {quote.email && (
              <Button onClick={() => setEmailOpen(true)} variant="outline" className="border-blue-200 text-blue-900" data-testid="admin-email-open"><Mail className="w-4 h-4 mr-2"/>Email al cliente</Button>
            )}
            <Button onClick={() => setAiOpen(true)} className="bg-amber-500 hover:bg-amber-600" data-testid="admin-ai-open">
              <Sparkles className="w-4 h-4 mr-2"/> IA Assistente
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="p-6 lg:col-span-2">
            <h3 className="heading font-bold text-slate-900 mb-3">Riepilogo</h3>
            <p className="text-slate-700 whitespace-pre-wrap">{quote.summary}</p>
            <h4 className="heading font-bold text-slate-900 mt-6 mb-3">Storico chat cliente</h4>
            <div className="space-y-2 max-h-96 overflow-y-auto bg-slate-50 rounded p-3">
              {(quote.chat_history || []).map((m, i) => (
                <div key={i} className={`text-sm px-3 py-2 rounded ${m.role === "user" ? "bg-white border" : "bg-blue-50"}`}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{m.role === "user" ? "Cliente" : "IA"}</div>
                  <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Stato</label>
              <Select value={quote.status} onValueChange={v => update({ status: v })}>
                <SelectTrigger data-testid="admin-status-select"><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Categoria</label>
              <Select value={quote.category} onValueChange={v => update({ category: v })}>
                <SelectTrigger data-testid="admin-cat-select"><SelectValue/></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Città</label>
              <Input defaultValue={quote.city} onBlur={e => e.target.value !== quote.city && update({ city: e.target.value })} data-testid="admin-city-input"/>
            </div>
            <div className="text-xs text-slate-500 border-t pt-3">Creato: {new Date(quote.created_at).toLocaleString("it-IT")}</div>
          </Card>
        </div>
      </div>

      <AdminAiDialog open={aiOpen} onOpenChange={setAiOpen} quoteId={id}/>
      <AdminChatDialog open={chatOpen} onOpenChange={setChatOpen} quoteId={id} me={user}/>
      <EmailDraftDialog open={emailOpen} onOpenChange={setEmailOpen} quote={quote} onSent={() => setEmailOpen(false)}/>
    </div>
  );
}

function EmailDraftDialog({ open, onOpenChange, quote, onSent }) {
  const [prompt, setPrompt] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const draft = async () => {
    setDrafting(true);
    try {
      const r = await api.post(`/admin/quotes/${quote.quote_id}/ai-email-draft`, { prompt });
      setSubject(r.data.subject);
      setBody(r.data.body);
      toast.success("Bozza IA pronta");
    } catch (e) { toast.error(formatErr(e)); } finally { setDrafting(false); }
  };
  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Compila oggetto e corpo"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/quotes/${quote.quote_id}/send-email`, { subject, body });
      if (r.data?.ok) {
        toast.success("Email inviata al cliente!");
        onSent();
        setSubject(""); setBody(""); setPrompt("");
      } else {
        toast.warning(r.data?.warning || "Email non inviata");
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><Mail className="w-5 h-5 text-blue-800"/>Email al cliente</DialogTitle>
          <p className="text-xs text-slate-500">Destinatario: <span className="font-mono">{quote?.email}</span></p>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Istruzioni per l'IA (opzionale)</label>
            <Input placeholder="Es: proponi sopralluogo martedì pomeriggio" value={prompt} onChange={e=>setPrompt(e.target.value)} data-testid="email-prompt"/>
          </div>
          <Button onClick={draft} disabled={drafting} variant="outline" className="w-full border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900" data-testid="email-ai-draft-btn">
            {drafting ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Sparkles className="w-4 h-4 mr-2"/>}
            {subject ? "Rigenera bozza IA" : "Prepara bozza con IA"}
          </Button>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Oggetto</label>
            <Input value={subject} onChange={e=>setSubject(e.target.value)} data-testid="email-subject"/>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Corpo email</label>
            <RichTextEditor value={body} onChange={setBody} testId="email-body" minHeight={220}/>
          </div>
          <div className="border rounded-md p-3 bg-slate-50">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Anteprima</div>
            <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: body || "<p class='text-slate-400'>Nessuna bozza ancora</p>" }} data-testid="email-preview"/>
          </div>
          <Button onClick={send} disabled={busy || !subject.trim() || !body.trim()} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="email-send-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Send className="w-4 h-4 mr-2"/>}
            Invia email al cliente
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminAiDialog({ open, onOpenChange, quoteId }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    if (open) { api.get(`/admin/ai-assist/${quoteId}`).then(r => setMsgs(r.data)).catch(()=>{}); setPending([]); }
  }, [open, quoteId]);

  useEffect(() => { ref.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if ((!text.trim() && pending.length === 0) || busy) return;
    const t = text.trim() || (pending.length > 0 ? "Analizza gli allegati" : "");
    const attachments = pending.map(p => p.file_id);
    const optimisticAtts = [...pending];
    setText(""); setPending([]); setBusy(true);
    setMsgs(m => [...m, { role: "user", content: t, attachments: optimisticAtts }]);
    try {
      const r = await api.post("/admin/ai-assist", { quote_id: quoteId, message: t, attachments });
      setMsgs(m => [...m, { role: "assistant", content: r.data.reply }]);
    } catch { toast.error("Errore IA"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[75vh] flex flex-col p-0">
        <DialogHeader className="p-4 bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-lg">
          <DialogTitle className="text-white heading flex items-center gap-2"><Sparkles className="w-5 h-5"/>Assistente IA per il personale</DialogTitle>
          <p className="text-amber-50 text-xs">Chiedi suggerimenti, bozze email, stime. Puoi anche allegare foto o documenti da analizzare.</p>
        </DialogHeader>
        <div ref={ref} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="admin-ai-messages">
          {msgs.length === 0 && <div className="text-center text-sm text-slate-500 mt-8">Prova a chiedere: "Prepara una bozza email di risposta" o allega una foto del quadro elettrico</div>}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-amber-500 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                {m.content}
                <MessageAttachments attachments={m.attachments} side={m.role === "user" ? "right" : "left"}/>
              </div>
            </div>
          ))}
          {busy && <Loader2 className="w-4 h-4 animate-spin text-amber-500"/>}
        </div>
        <PendingAttachments items={pending} onRemove={(id) => setPending(p => p.filter(x => x.file_id !== id))}/>
        <div className="p-3 border-t flex gap-2 items-center">
          <AttachButton onUploaded={(a) => setPending(p => [...p, ...a])} testId="admin-ai-attach"/>
          <Input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter" && send()} placeholder="Chiedi qualcosa o allega un file..." data-testid="admin-ai-input"/>
          <Button onClick={send} disabled={busy || (!text.trim() && pending.length === 0)} data-testid="admin-ai-send" className="bg-amber-500 hover:bg-amber-600"><Send className="w-4 h-4"/></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AdminChatDialog({ open, onOpenChange, quoteId, me }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]);

  const load = async () => {
    if (!open) return;
    try { const r = await api.get(`/messages/${quoteId}`); setMessages(r.data); } catch {}
  };
  useEffect(() => { load(); setPending([]); /* eslint-disable-next-line */ }, [open, quoteId]);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(load, 5000); return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, quoteId]);

  const send = async () => {
    if (!text.trim() && pending.length === 0) return;
    setBusy(true);
    try {
      await api.post(`/messages/${quoteId}`, {
        content: text.trim(),
        attachments: pending.map(p => p.file_id),
      });
      setText("");
      setPending([]);
      await load();
    } catch { toast.error("Errore"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-[70vh] flex flex-col p-0">
        <DialogHeader className="p-4 bg-blue-800 rounded-t-lg">
          <DialogTitle className="text-white heading">Chat con il cliente</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50" data-testid="admin-chat-messages">
          {messages.length === 0 && <div className="text-center text-sm text-slate-500 mt-8">Nessun messaggio ancora.</div>}
          {messages.map(m => {
            const mine = m.from_user_id === me?.user_id;
            return (
              <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${mine ? "bg-blue-800 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  <div className="text-[10px] opacity-70 mb-0.5">{m.from_name}{m.from_staff && " · Staff"}</div>
                  {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
                  <MessageAttachments attachments={m.attachments} side={mine ? "right" : "left"}/>
                </div>
              </div>
            );
          })}
        </div>
        <PendingAttachments items={pending} onRemove={(id) => setPending(p => p.filter(x => x.file_id !== id))}/>
        <div className="p-3 border-t flex gap-2 items-center">
          <AttachButton onUploaded={(a) => setPending(p => [...p, ...a])} testId="admin-chat-attach"/>
          <Input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter" && send()} placeholder="Rispondi al cliente..." data-testid="admin-chat-input"/>
          <Button onClick={send} disabled={busy || (!text.trim() && pending.length === 0)} data-testid="admin-chat-send" className="bg-blue-800">{busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
